import { useState, forwardRef, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { useTranslation } from 'react-i18next';

//local
import { onChartLoad, onInit, setFilterDate, setFilterGroup, setFilterRestaurant } from '../../store/Dashboard/actions';

//styles
import './styles.css';

//components
import { ChartArea } from './ChartArea';
import { ChartDonut } from './ChartDonut';
import Loading from '../../components/Loading';
import { RoundedBox } from '../../components/Shared/RoundedBox';
import { Breadcrumbs } from '../../components/Breadcrumbs';
import Button from '../../components/Button/Button';
import { CustomSelect } from '../../components/Shared/CustomSelect';
import { useGetSettings } from '../../network';
import { ChartBar } from './ChartBar';
import { FormField } from '../../components/FormField';
import { OverLay } from '../../components/OverLay';

const customSelectStyles = {
    control: (base) => ({
        ...base,
        border: '0',
        boxShadow: '0px 1px 3px rgba(16, 24, 40, 0.1), 0px 1px 2px rgba(16, 24, 40, 0.06)',
        backgroundColor: '#F9FAFB',
        color: '#1D2530',
        height: 40,
        borderRadius: 8,
        '&:hover': {
            border: '0',
            cursor: 'pointer'
        },
    }),
    singleValue: (provided) => ({
        ...provided,
        fontSize: 14,
        fontWeight: 500,
        color: '#1D2530',
        textAlign: 'center',
    }),
};

export const Dashboards = () => {
    const { t, i18n } = useTranslation();
    const dispatch = useDispatch();

    useEffect(() => {
        dispatch(onInit());
        dispatch(onChartLoad());
    }, []);

    const { data: settings = {}, isLoading: isSettingsLoading } = useGetSettings();
    const currency = settings.currency?.title;

    const { loading, loadingCharts, restaurants, currentTotal, chartOrdersCount, chartSales, chartTotalSales } = useSelector((state) => ({
        loading: state.dashboard.loading,
        loadingCharts: state.dashboard.loadingCharts,
        restaurants: state.dashboard.restaurants,
        currentTotal: state.dashboard.currentTotal,
        chartOrdersCount: state.dashboard.chartOrdersCount,
        chartSales: state.dashboard.chartSales,
        chartTotalSales: state.dashboard.chartTotalSales,
    }));

    let options = [{ value: null, label: t('All Stores') }];
    restaurants.forEach((item) =>
        options.push({
            value: item.id,
            label: item.name,
        })
    );

    const chartGroups = [
        { value: 'daily', label: t('Daily') },
        { value: 'weekly', label: t('Weekly') },
        { value: 'monthly', label: t('Monthly') },
        { value: 'yearly', label: t('Yearly') },
    ];

    const [startDate, setStartDate] = useState(new Date());
    const ExampleCustomInput = forwardRef(({ value, onClick }, ref) => (
        <button className="px-4 pt-[10px] pb-[9px] text bg-white rounded-md shadow bg-gray-50 text-sm font-medium" onClick={onClick} ref={ref}>
            {value}
        </button>
    ));

    const handleChange = (newValue) => {
        dispatch(setFilterRestaurant(newValue.value));
        dispatch(onInit());
    };
    const handleChangeGroup = (newValue) => {
        dispatch(setFilterGroup(newValue.value));
        dispatch(onChartLoad());
    };

    const onChangeDate = (date) => {
        setStartDate(date);
        dispatch(setFilterDate(date));
        dispatch(onInit());
    };

    const pages = [{ name: t('Dashboard'), path: '/chain/dashboard' }];

    return (
        <main className="max-w-7xl px-4 sm:px-8 lg:px-10 xl:px-12">
            <Breadcrumbs pages={pages} />
            <div className="flex justify-between gap-6">
                <div className="flex gap-2">
                    <CustomSelect className="min-w-[150px]" onChange={handleChange} options={options} defaultValue={options[0]} isSearchable={false} config={customSelectStyles} />
                    <CustomSelect className="min-w-[120px]" onChange={handleChangeGroup} options={chartGroups} defaultValue={chartGroups[0]} isSearchable={false} config={customSelectStyles} />
                    <DatePicker locale={i18n.language} selected={startDate} onChange={onChangeDate} customInput={<ExampleCustomInput />} />
                </div>
                <FormField disabled popoverText="On development stage">
                    <Button isUpperCase={false}>{t('Generate Report')}</Button>
                </FormField>
            </div>

            <div className="mt-5">
                <Loading loading={loading}>
                    <div className="flex gap-6">
                        <RoundedBox className="min-h-[110px] min-w-[260px] w-full flex flex-col gap-4 min-w-[260px]">
                            <div className="text-sm">{t('Total orders')}</div>
                            <div className="font-bold">{currentTotal && currentTotal.orders ? currentTotal.orders : 0}</div>
                        </RoundedBox>
                        <RoundedBox className="min-h-[110px] min-w-[260px] w-full flex flex-col gap-4 ">
                            <div className="text-sm">{t('Accepted orders')}</div>
                            <div className="font-bold">{currentTotal && currentTotal.accepted ? currentTotal.accepted : 0}</div>
                        </RoundedBox>
                        <RoundedBox className="min-h-[110px] min-w-[260px] w-full flex flex-col gap-4">
                            <div className="text-sm">{t('Total sales')}</div>
                            <div className="font-bold">
                                {currency}
                                {currentTotal && currentTotal.sales ? currentTotal.sales : 0}
                            </div>
                        </RoundedBox>
                        <RoundedBox className="min-h-[110px] min-w-[260px] w-full flex flex-col gap-4">
                            <div className="text-sm">{t('Average order values')}</div>
                            <div className="font-bold">
                                {currency}
                                {currentTotal && currentTotal.avg ? currentTotal.avg : 0}
                            </div>
                        </RoundedBox>
                    </div>
                </Loading>
            </div>

            <div className="flex gap-5 mt-5">
                <RoundedBox className="w-7/12">
                    <Loading loading={loadingCharts}>
                        <ChartArea title={t('Total sales')} labels={chartTotalSales.labels} data={chartTotalSales.data} />
                    </Loading>
                </RoundedBox>
                <RoundedBox className="w-5/12">
                    <Loading loading={loadingCharts}>
                        <ChartDonut title={t('Sales by Store')} labels={chartSales.labels} data={chartSales.data} />
                    </Loading>
                </RoundedBox>
            </div>

            <div className="flex gap-5 mt-5 mb-8">
                <RoundedBox className="w-7/12">
                    <Loading loading={loadingCharts}>
                        <ChartArea title={t('Total Orders')} labels={chartOrdersCount.labels} data={chartOrdersCount.data} />
                    </Loading>
                </RoundedBox>
                <RoundedBox className="w-5/12">
                    <OverLay text="On development stage" textClassName="text-md">
                        {!isSettingsLoading && <ChartBar title={t('Sales by Order Type')} labels={['Dine-in', 'Pickup', 'Delivery']} data={[18000, 15000, 3000]} currency={currency} />}
                    </OverLay>
                </RoundedBox>
            </div>
        </main>
    );
};
